<template>
  <van-tabbar v-model="active" active-color="#fd6864" class="custom-tabbar" @change="handleTabChange">
    <van-tabbar-item class="custom-tabbar-item">
      <span>春信</span>
      <template #icon="props">
        <img class="tab-icon" :src="props.active ? tabIcons.chunxin.active : tabIcons.chunxin.normal" alt="春信" />
      </template>
    </van-tabbar-item>
    <van-tabbar-item class="custom-tabbar-item">
      <span>推荐</span>
      <template #icon="props">
        <img class="tab-icon" :src="props.active ? tabIcons.recom.active : tabIcons.recom.normal" alt="推荐" />
      </template>
    </van-tabbar-item>
    <van-tabbar-item v-if="socialEnabled" class="custom-tabbar-item">
      <span>视频</span>
      <template #icon="props">
        <img class="tab-icon" :src="props.active ? tabIcons.video.active : tabIcons.video.normal" alt="视频" />
      </template>
    </van-tabbar-item>
    <van-tabbar-item class="custom-tabbar-item" :badge="unreadCount>0?unreadCount:''">
      <span>消息</span>
      <template #icon="props">
        <img class="tab-icon" :src="props.active ? tabIcons.message.active : tabIcons.message.normal" alt="消息" />
      </template>
    </van-tabbar-item>
    <van-tabbar-item class="custom-tabbar-item">
      <span>我的</span>
      <template #icon="props">
        <img class="tab-icon" :src="props.active ? tabIcons.my.active : tabIcons.my.normal" alt="我的" />
      </template>
    </van-tabbar-item>
  </van-tabbar>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useUserStore } from "@/stores/user";
import { countUnRead } from "@/api/message";
import { socialOnState } from "@/api/appVersion";

// tabbar 图标（默认金色线框 / 选中红渐变实心）
import iconChunxin from "@/assets/images/tarbar/1.png";
import iconChunxinActive from "@/assets/images/tarbar/1-sed.png";
import iconRecom from "@/assets/images/tarbar/2.png";
import iconRecomActive from "@/assets/images/tarbar/2-sed.png";
import iconVideo from "@/assets/images/tarbar/3.png";
import iconVideoActive from "@/assets/images/tarbar/3-sed.png";
import iconMessage from "@/assets/images/tarbar/4.png";
import iconMessageActive from "@/assets/images/tarbar/4-sed.png";
import iconMy from "@/assets/images/tarbar/5.png";
import iconMyActive from "@/assets/images/tarbar/5-sed.png";

const tabIcons = {
  chunxin: { normal: iconChunxin, active: iconChunxinActive },
  recom: { normal: iconRecom, active: iconRecomActive },
  video: { normal: iconVideo, active: iconVideoActive },
  message: { normal: iconMessage, active: iconMessageActive },
  my: { normal: iconMy, active: iconMyActive },
};

// 社交功能是否启用（响应式，版本检测返回后自动更新）
const socialEnabled = socialOnState;

const router = useRouter();
const route = useRoute();
const userStore = useUserStore();

// Tab 索引到路由的映射（社交功能开启时）
const tabRoutesWithSocial = ['/health', '/', '/video', '/message-center', '/my'];

// Tab 索引到路由的映射（社交功能关闭时）
const tabRoutesWithoutSocial = ['/health', '/', '/message-center', '/my'];

// 根据社交功能状态获取对应的路由映射
const tabRoutes = computed(() => socialEnabled.value ? tabRoutesWithSocial : tabRoutesWithoutSocial);

// 根据当前路由获取应该高亮的 tab 索引
const getCurrentTabIndex = () => {
  const path = route.path;
  // 处理特殊路由映射
  if (path === '/' || path === '/top' || path.startsWith('/top?') || path.startsWith('/?')) {
    // 社交功能关闭时，推荐页是索引1，社交功能开启时也是索引1
    return 1; // 推荐页
  }
  // 如果社交功能关闭且当前是视频页面，跳转到推荐页
  if (!socialEnabled.value && (path === '/video' || path.startsWith('/video?'))) {
    return 1; // 跳转到推荐页
  }
  const index = tabRoutes.value.findIndex(routePath => path === routePath || path.startsWith(routePath + '?'));
  return index >= 0 ? index : 1; // 默认选中推荐页
};

const active = ref(getCurrentTabIndex()); // 根据当前路由高亮对应菜单项

const unreadCount = ref(0); // 消息未读数量

// 轮询配置
const POLLING_INTERVAL = 5000; // 5秒轮询
let pollingTimer = null;
let isPollingStopped = false;

// 获取未读消息数
const fetchUnreadCount = async () => {
  if (isPollingStopped || !userStore.token || !pollingTimer) {
    return;
  }
  try {
    // 调用接口获取未读数量
    const count = await countUnRead();
    unreadCount.value = count;
  } catch (error) {
    console.error('获取消息未读数量失败：', error);
    unreadCount.value = 0; // 异常时置0，避免角标显示错误
  }
};

// 开始轮询
const startPolling = () => {
  if (pollingTimer || document.hidden || !userStore.token) return;
  isPollingStopped = false;
  fetchUnreadCount(); // 立即执行一次
  pollingTimer = setInterval(fetchUnreadCount, POLLING_INTERVAL);
};

// 停止轮询
const stopPolling = () => {
  if (pollingTimer) {
    clearInterval(pollingTimer);
    pollingTimer = null;
    isPollingStopped = true;
  }
};

// 页面可见性变化处理
const handleVisibilityChange = () => {
  if (document.hidden) {
    stopPolling();
  } else if (userStore.token) {
    startPolling();
  }
};

// 组件挂载
onMounted(() => {
  if (userStore.token) {
    startPolling();
  }
  // 监听页面可见性变化
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

// 组件卸载
onUnmounted(() => {
  stopPolling();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});

// 处理 tab 点击
const handleTabChange = (index) => {
  const targetRoute = tabRoutes.value[index];
  if (targetRoute) {
    router.push(targetRoute);
  }
};

// 监听路由变化/社交开关变化，确保tab高亮正确
watch(
  [() => route.path, socialEnabled],
  () => {
    active.value = getCurrentTabIndex();
    // 每次路由变化时刷新未读数量
    fetchUnreadCount();
  }
);

// 监听用户登录状态变化，停止
watch(
  () => userStore.token,
  () => {
    // 直接从 localStorage 检查 token，如果为空就不轮询
    const hasToken = !!localStorage.getItem('token');
    if (!hasToken) {
      stopPolling();
    } else if (!document.hidden) {
      startPolling();
    }
  }
);
</script>

<style scoped>
.custom-tabbar {
  height: 70px;
  padding-bottom: calc(-10px + var(--safe-area-inset-bottom, 0px));
  box-sizing: content-box;
}

.custom-tabbar-item {
  flex-direction: column;
  justify-content: center;
  padding-top: 6px;
}

.tab-icon {
  width: 24px;
  height: 24px;
  display: block;
}

:deep(.van-tabbar-item__text) {
  font-size: 14px;
  margin-top: 2px;
}
</style>
